import Box from '../../atoms/Box/Box';
import Dropdown from '../../atoms/Dropdown/Dropdown';
import IconButton from '../../atoms/IconButton/IconButton';
import Typography from '../../atoms/Typography/Typography';
import styles from './Pagination.module.scss';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export type PaginationProps = {
  totalPages: number;
  currentPage: number;
  handlePageChange: (page: number) => void;
  theme?: 'dark' | 'light';
  /** Page size options for the dropdown. Defaults to [10, 25, 50, 100] */
  pageSizeOptions?: number[];
  /** Current page size */
  pageSize?: number;
  /** Callback when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
  /** Total number of items (used for "X of Y items" display) */
  totalItems?: number;
};

/**
 * AG Grid style pagination component with first/prev/next/last navigation
 * @component
 * @param {number} totalPages - Total number of pages
 * @param {number} currentPage - Current page (0-indexed)
 * @param {function} handlePageChange - Callback when page changes
 * @param {'dark' | 'light'} theme - Theme variant
 * @param {number[]} pageSizeOptions - Array of page size options
 * @param {number} pageSize - Current page size
 * @param {function} onPageSizeChange - Callback when page size changes
 * @param {number} totalItems - Total number of items
 * @returns {JSX.Element | null}
 */
const Pagination = ({
  totalPages,
  currentPage,
  handlePageChange,
  theme = 'dark',
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  pageSize,
  onPageSizeChange,
  totalItems,
}: PaginationProps) => {
  // Don't render pagination if there's only one page or less and no page size selector
  if (totalPages <= 1 && !onPageSizeChange) {
    return null;
  }

  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage >= totalPages - 1;

  const goToFirstPage = () => handlePageChange(0);
  const goToPrevPage = () => handlePageChange(currentPage - 1);
  const goToNextPage = () => handlePageChange(currentPage + 1);
  const goToLastPage = () => handlePageChange(totalPages - 1);

  const handlePageSizeChange = (value: string) => {
    const newPageSize = Number(value);
    onPageSizeChange?.(newPageSize);
    // Note: Page reset is handled by the parent component (Table)
  };

  // Convert page size options to dropdown format
  const pageSizeDropdownOptions = pageSizeOptions.map((size) => ({
    label: size.toString(),
    value: size.toString(),
  }));

  // Calculate item range for display
  const getItemRange = () => {
    if (!totalItems || !pageSize) return null;
    const start = currentPage * pageSize + 1;
    const end = Math.min((currentPage + 1) * pageSize, totalItems);
    return { start, end };
  };

  const itemRange = getItemRange();

  return (
    <Box className={styles.paginationWrapper} data-theme={theme}>
      <Box className={styles.paginationContent}>
        {/* Left: Item Range Display */}
        <Box className={styles.leftSection}>
          {itemRange && (
            <Typography variant="body" size="medium" theme={theme} color="white">
              Showing {itemRange.start}-{itemRange.end} of {totalItems}
            </Typography>
          )}
        </Box>

        {/* Right: Page Size Selector and Navigation Controls */}
        <Box className={styles.rightSection}>
          {/* Page Size Selector */}
          {onPageSizeChange && pageSize && (
            <Box className={styles.pageSizeSelector}>
              <Dropdown
                theme={theme}
                selectedOption={pageSize.toString()}
                handleSelect={handlePageSizeChange}
                options={pageSizeDropdownOptions}
                size="small"
                name="pagination-page-size"
                showValidation={false}
              />
            </Box>
          )}

          {/* Navigation Controls */}
          {totalPages > 1 && (
            <Box className={styles.navigationControls}>
              {/* First Page Button */}
              <IconButton
                icon={{ category: 'arrows', name: 'chevron-bar-left' }}
                buttonType="tertiary"
                size="small"
                theme={theme}
                handleClick={goToFirstPage}
                gaComponent="pagination-first"
                dataTestId="pagination-first"
                disabled={isFirstPage}
              />

              {/* Previous Button */}
              <IconButton
                icon={{ category: 'arrows', name: 'chevron-left' }}
                buttonType="tertiary"
                size="small"
                theme={theme}
                handleClick={goToPrevPage}
                gaComponent="pagination-prev"
                dataTestId="pagination-prev"
                disabled={isFirstPage}
              />

              {/* Page X of Y */}
              <Typography variant="body" size="medium" theme={theme} color="white">
                Page {currentPage + 1} of {totalPages}
              </Typography>

              {/* Next Button */}
              <IconButton
                icon={{ category: 'arrows', name: 'chevron-right' }}
                buttonType="tertiary"
                size="small"
                theme={theme}
                handleClick={goToNextPage}
                gaComponent="pagination-next"
                dataTestId="pagination-next"
                disabled={isLastPage}
              />

              {/* Last Page Button */}
              <IconButton
                icon={{ category: 'arrows', name: 'chevron-bar-right' }}
                buttonType="tertiary"
                size="small"
                theme={theme}
                handleClick={goToLastPage}
                gaComponent="pagination-last"
                dataTestId="pagination-last"
                disabled={isLastPage}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Pagination;
